import { z } from "zod";

/** A broken export response must stop monitoring instead of retrying forever. */
export class ExportResponseError extends Error {
  constructor() {
    super("Réponse inattendue du serveur");
  }
}

const count = z.number().int().nonnegative().safe();
const activeStatus = z.enum(["queued", "running"]);
const exportIdentity = z.object({ export_id: z.string().trim().min(1), type: z.literal("invoices") });
const exportStart = exportIdentity.extend({ status: z.literal("queued"), total: count, processed: count.default(0) });
const exportStatus = exportIdentity.extend({
  status: z.enum(["queued", "running", "ready", "failed", "cancelled", "expired"]),
  processed: count,
  total: count,
  file_name: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
  retryable: z.boolean().optional(),
  expires_at: z.string().datetime({ offset: true }).nullable().optional(),
});

function parseExportResponse<T>(schema: z.ZodType<T, z.ZodTypeDef, unknown>, data: unknown): T {
  const result = schema.safeParse(data);
  if (!result.success) throw new ExportResponseError();
  return result.data;
}

/** Start and retry acknowledgements decode blobs; only invoice routes imply their type. */
export async function parseExportAck(data: unknown, { conflict = false, impliedType }: { conflict?: boolean; impliedType?: "invoices" } = {}) {
  try {
    const payload = data instanceof Blob ? JSON.parse(await data.text()) : data;
    return parseExportResponse(exportStart.extend({
      status: conflict ? activeStatus : z.literal("queued"),
      type: impliedType ? z.literal("invoices").default(impliedType) : z.literal("invoices"),
    }), payload);
  }
  catch {
    throw new ExportResponseError();
  }
}

/** Status checks require the expected export id. */
export function parseExportStatus(data: unknown, expectedId: string) {
  const result = parseExportResponse(exportStatus, data);
  if (result.export_id !== expectedId) throw new ExportResponseError();
  return result;
}
